import { html, nothing, PropertyValueMap, unsafeCSS } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'

import { DdsElement } from '@/internal/dds-hu-element'
import { translate } from '@/localization/localization'

import styles from './pager.scss?inline'

/**
 * `dap-ds-pager`
 * @summary A pager is a component that displays pagination controls.
 * @element dap-ds-pager
 * @title - Pager
 *
 * @event {{ action: string, pagination: { pageIndex: number, pageSize: number } }} dds-pagination-change - Event fired when the pagination changes
 *
 * @csspart base - The main pager container.
 * @csspart first - The first page button.
 * @csspart previous - The previous page button.
 * @csspart next - The next page button.
 * @csspart last - The last page button.
 * @csspart page-size - The page size select.
 * @csspart pager-button-first-base - The base of the first page button.
 * @csspart pager-button-first-content - The content of the first page button.
 * @csspart pager-button-previous-base - The base of the previous page button.
 * @csspart pager-button-previous-content - The content of the previous page button.
 * @csspart pager-button-next-base - The base of the next page button.
 * @csspart pager-button-next-content - The content of the next page button.
 * @csspart pager-button-last-base - The base of the last page button.
 * @csspart pager-button-last-content - The content of the last page button.
 * @csspart page-size-select-base - The base of the page size select.
 * @csspart page-size-select-trigger - The trigger of the page size select.
 *
 * @cssproperty --dds-pager-spacing-vertical - The vertical spacing of the pager (default: var(--dds-spacing-400))
 * @cssproperty --dds-pager-spacing-horizontal - The horizontal spacing of the pager (default: var(--dds-spacing-200))
 * @cssproperty --dds-pager-button-padding - The padding of the pager buttons (default: var(--dds-spacing-150))
 * @cssproperty --dds-pager-button-border-width - The border width of the pager buttons (default: var(--dds-border-width-base, 1px))
 * @cssproperty --dds-pager-button-border-color - The border color of the pager buttons (default: var(--dds-button-subtle-border-neutral-enabled))
 * @cssproperty --dds-pager-button-border-radius - The border radius of the pager buttons (default: var(--dds-radius-rounded))
 * @cssproperty --dds-pager-button-background - The background color of the pager buttons (default: var(--dds-button-subtle-background-neutral-enabled))
 * @cssproperty --dds-pager-button-font-size - The font size of the pager buttons (default: var(--dds-font-sm, 14px))
 * @cssproperty --dds-pager-button-font-weight - The font weight of the pager buttons (default: var(--dds-font-weight-bold, 700))
 */
@customElement('dap-ds-pager')
export default class DapDSPager extends DdsElement {
  /** Whether the pager is disabled. */
  @property({ type: Boolean }) disabled = false
  /** Enable manual pagination. If true, the component will not automatically update the page index. */
  @property({ type: Boolean }) manualPagination = false
  /** The current page index. */
  @property({ type: Number }) pageIndex = 0
  @state() private _pageIndex = 0
  /** The number of items per page. */
  @property({ type: Number }) pageSize = 10
  @state() private _pageSize = 10
  /** The total number of rows. */
  @property({ type: Number }) totalRows = 0
  /** Available page size options */
  @property({ type: Array }) pageSizeOptions = [10, 25, 50, 100]

  /** Show the page size options. */
  @property({ type: String }) showPageSizeOptions = 'true'
  /** Show the page index. */
  @property({ type: String }) showPageIndex = 'true'
  /** Show the page count. */
  @property({ type: String }) showPageCount = 'true'
  /** Show the first button. */
  @property({ type: String }) showFirstButton = 'true'
  /** Show the previous button. */
  @property({ type: String }) showPreviousButton = 'true'
  /** Show the next button. */
  @property({ type: String }) showNextButton = 'true'
  /** Show the last button. */
  @property({ type: String }) showLastButton = 'true'

  /** The first button label. */
  @property({ type: String }) firstButtonLabel: string
  /** The previous button label. */
  @property({ type: String }) previousButtonLabel: string
  /** The next button label. */
  @property({ type: String }) nextButtonLabel: string
  /** The last button label. */
  @property({ type: String }) lastButtonLabel: string

  /** The function to determine the pager text. If not provided, uses default reactive translation.
   * @type {Function}
   */
  @property({ attribute: false }) pageStateText?: (
    pageIndex: number,
    pageSize: number,
    totalRows: number,
  ) => unknown

  static readonly styles = unsafeCSS(styles)

  protected updated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.updated(_changedProperties)

    if (this.manualPagination) {
      if (_changedProperties.has('pageIndex')) {
        this._pageIndex = this.pageIndex
      }

      if (_changedProperties.has('pageSize')) {
        this._pageSize = this.pageSize
      }
    }
  }

  private updatePageIndex(action: string, pageIndex: number, pageSize: number) {
    this.manualPagination
      ? (this._pageIndex = pageIndex)
      : (this.pageIndex = pageIndex)

    this.emit('dds-pagination-change', {
      action,
      pagination: {
        pageIndex: pageIndex,
        pageSize: pageSize,
      },
    })
  }

  private updatePageSize(pageSize: number) {
    // Reset to first page when changing page size
    const pageIndex = 0

    if (this.manualPagination) {
      this._pageSize = pageSize
      this._pageIndex = pageIndex
    } else {
      this.pageSize = pageSize
      this.pageIndex = pageIndex
    }

    this.emit('dds-pagination-change', {
      action: 'pageSize',
      pagination: {
        pageIndex: pageIndex,
        pageSize: pageSize,
      },
    })
  }

  private renderPageSizeSelect() {
    if (this.showPageSizeOptions !== 'true') return nothing

    return html`<dap-ds-select
      class="pager__select"
      part="page-size"
      exportparts="base:page-size-select-base, trigger:page-size-select-trigger"
      floatingStrategy="fixed"
      noAnimation
      ?disabled=${this.disabled}
      size="xs"
      .value=${this.manualPagination
        ? this._pageSize.toString()
        : this.pageSize.toString()}
      @dds-change=${(e: Event) => {
        if ((e.target as HTMLSelectElement).value) {
          this.updatePageSize(Number((e.target as HTMLSelectElement).value))
        }
      }}>
      <dap-ds-icon-arrow-down-s-fill
        slot="indicator-icon"
        size="16"></dap-ds-icon-arrow-down-s-fill>
      ${this.pageSizeOptions && this.pageSizeOptions.length > 0
        ? this.pageSizeOptions.map(
            size =>
              html`<dap-ds-option-item
                value=${size}
                label=${translate('pager.pageSize', { count: size })}
                >${size}</dap-ds-option-item
              >`,
          )
        : nothing}
    </dap-ds-select>`
  }

  private renderNavigationButton(
    part: string,
    action: string,
    pageIndex: number,
    disabled: boolean,
    label: unknown,
    slotName: string,
  ) {
    return html`<dap-ds-button
      part=${part}
      size="xs"
      exportparts="base:pager-button-${part}-base, content:pager-button-${part}-content"
      variant="subtle-neutral"
      @click=${() => this.updatePageIndex(action, pageIndex, this.pageSize)}
      ?disabled=${disabled || this.disabled}>
      <slot name=${slotName}> ${label} </slot>
    </dap-ds-button>`
  }

  private renderPageInfo() {
    if (this.showPageIndex !== 'true') return nothing

    const pageIndex = this.manualPagination ? this._pageIndex : this.pageIndex
    const pageSize = this.manualPagination ? this._pageSize : this.pageSize

    // If custom pageStateText is provided, use it (for backwards compatibility)
    if (this.pageStateText) {
      const customText = this.pageStateText(pageIndex, pageSize, this.totalRows)
      return html`<span>${customText}</span>`
    }

    // Otherwise use reactive translate()
    return html`<span
      >${translate('pager.pageInfo', {
        rangeStart: pageIndex * pageSize + 1,
        rangeEnd: (pageIndex + 1) * pageSize,
        totalRows: this.totalRows,
      })}</span
    >`
  }

  protected render() {
    const isLastPage =
      this.pageIndex === Math.ceil(this.totalRows / this.pageSize) - 1

    return html`
      <div part="base" class="pager">
        ${this.renderPageSizeSelect()}
        ${this.showFirstButton === 'true'
          ? this.renderNavigationButton(
              'first',
              'first',
              0,
              this.pageIndex === 0,
              this.firstButtonLabel || translate('pager.first'),
              'first-button-label',
            )
          : nothing}
        ${this.showPreviousButton === 'true'
          ? this.renderNavigationButton(
              'previous',
              'previous',
              this.pageIndex - 1,
              this.pageIndex === 0,
              this.previousButtonLabel || translate('pager.previous'),
              'previous-button-label',
            )
          : nothing}
        ${this.renderPageInfo()}
        ${this.showNextButton === 'true'
          ? this.renderNavigationButton(
              'next',
              'next',
              this.pageIndex + 1,
              isLastPage,
              this.nextButtonLabel || translate('pager.next'),
              'next-button-label',
            )
          : nothing}
        ${this.showLastButton === 'true'
          ? this.renderNavigationButton(
              'last',
              'last',
              Math.ceil(this.totalRows / this.pageSize) - 1,
              isLastPage,
              this.lastButtonLabel || translate('pager.last'),
              'last-button-label',
            )
          : nothing}
      </div>
    `
  }
}
